// Live data client for the Stuck Workflow dashboard.
// Calls the SherlockWebappService GET /stuck via the app's signed request layer
// (auto-targets http://localhost:8080 in local dev, api-{stage}-{realm}... in prod).
import { makeAwsSignedRequest } from '../../services/ApiService';
import { StuckRow, HistoryPoint, Region, Workflow, TicketInfo, Rail } from './types';
import { WORKFLOW } from './constants';
import { railOf, resolvePlugin } from './stuckLayout';

/** Orca realms collapse to our 3 dashboard regions (EU-DUB/EU-ZAZ → EU). */
const normalizeRegion = (raw: string): Region | null => {
    if (raw === 'NA') return 'NA';
    if (raw === 'FE') return 'FE';
    if (raw && raw.toUpperCase().startsWith('EU')) return 'EU';
    return null;
};

interface RawStuckRow {
    region: string;
    workflow: string;
    plugin: string;
    operation?: string;
    state?: string;
    count?: number;
    isError?: boolean;
}

export interface StuckPayload {
    rows: StuckRow[];
    history: HistoryPoint[];
    tickets: TicketInfo[];
    generatedAt: string;
    /** Raw rows dropped during mapping (non-error state or unrecognized region). 0 = none. */
    droppedRows: number;
}

/** Map one raw /stuck row to a StuckRow, or null to drop (non-error / unknown region). */
const mapRow = (r: RawStuckRow): StuckRow | null => {
    if (!r.isError) return null;
    const region = normalizeRegion(r.region);
    if (!region) return null;
    const workflow = (r.workflow === WORKFLOW.NATIVE ? WORKFLOW.NATIVE : WORKFLOW.PCES) as Workflow;
    const rail = railOf(workflow);
    return {
        region,
        workflow,
        rail,
        plugin: resolvePlugin(r.plugin, rail),
        op: r.operation || '—',
        state: r.state || '(no state)',
        stuck: Number(r.count || 0),
    };
};

interface RawTicket {
    region?: string;
    rail?: string;
    plugin?: string;
    ticketFound?: boolean;
    ticketCount?: number;
    ticketId?: string | null;
    ticketStatus?: string | null;
    ticketGroup?: string | null;
    ticketSeverity?: string | null;
    ticketUrl?: string | null;
    simSearchUrl?: string;
    ticketList?: Array<{
        ticketId?: string | null;
        ticketStatus?: string | null;
        ticketSeverity?: string | null;
        ticketGroup?: string | null;
        ticketUrl?: string | null;
    }>;
}

/** Map one raw backend ticket to a TicketInfo, or null to drop (unrecognized region/rail). */
const mapTicket = (t: RawTicket): TicketInfo | null => {
    const region = normalizeRegion(t.region || '');
    if (!region) return null;
    const rail: Rail = t.rail === WORKFLOW.NATIVE || t.rail === 'Native' ? 'Native' : 'PCES';
    return {
        region,
        rail,
        plugin: t.plugin || '(no plugin)',
        ticketFound: Boolean(t.ticketFound),
        ticketCount: Number(t.ticketCount || 0),
        ticketId: t.ticketId ?? null,
        ticketStatus: t.ticketStatus ?? null,
        ticketGroup: t.ticketGroup ?? null,
        ticketSeverity: t.ticketSeverity ?? null,
        ticketUrl: t.ticketUrl ?? null,
        simSearchUrl: t.simSearchUrl || '',
        ticketList: Array.isArray(t.ticketList)
            ? t.ticketList.map((r) => ({
                ticketId: r.ticketId ?? null,
                ticketStatus: r.ticketStatus ?? null,
                ticketSeverity: r.ticketSeverity ?? null,
                ticketGroup: r.ticketGroup ?? null,
                ticketUrl: r.ticketUrl ?? null,
            }))
            : [],
    };
};

export const toPayload = (d: any): StuckPayload => {
    if (d && d.error) throw new Error(String(d.error));
    const raw: RawStuckRow[] = Array.isArray(d && d.rows) ? d.rows : [];
    const rows: StuckRow[] = [];
    let droppedNonError = 0;
    let droppedUnknownRegion = 0;
    for (const r of raw) {
        if (!r.isError) { droppedNonError++; continue; }
        if (!normalizeRegion(r.region)) { droppedUnknownRegion++; continue; }
        rows.push(mapRow(r) as StuckRow);
    }
    const droppedRows = droppedNonError + droppedUnknownRegion;
    if (droppedRows > 0) {
        // Surface silent data loss rather than dropping rows unnoticed.
        console.warn(`[stuck] dropped ${droppedRows} raw row(s): ${droppedNonError} non-error, ${droppedUnknownRegion} unrecognized region`);
    }
    const history: HistoryPoint[] = Array.isArray(d && d.history) ? d.history : [];
    const tickets: TicketInfo[] = (Array.isArray(d && d.tickets) ? d.tickets : [])
        .map(mapTicket)
        .filter((t: TicketInfo | null): t is TicketInfo => t !== null);
    return { rows, history, tickets, generatedAt: (d && d.generatedAt) || '', droppedRows };
};

/** Fetch and map the live /stuck payload from the backend. Throws on network/parse error. */
export async function fetchStuck(stage: string, realm: string): Promise<StuckPayload> {
    const d = await makeAwsSignedRequest(stage, realm, 'stuck');
    return toPayload(d);
}
